"use client";
import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Article, Category } from "@/types";

// 🔄 Squelettes de chargement
const Skeleton = () => (
  <div className="h-6 w-full animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
);

const RightSidebar = () => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [recentArticles, setRecentArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingRecentArticles, setLoadingRecentArticles] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 🔄 Fonction de requête API optimisée avec `useCallback`
  const fetchData = useCallback(
    async <T,>(
      url: string,
      setter: (data: T) => void,
      setLoading: (loading: boolean) => void,
    ) => {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

        const data = await response.json();
        if (!data.success || (!data.categories && !data.articles)) {
          console.warn(`⚠️ API ${url} a retourné success: false`);
          return;
        }

        setter(data.categories || data.articles);
      } catch (error) {
        setErrorMessage(
          "Impossible de charger les données. Vérifiez votre connexion.",
        );
        console.error(`❌ Erreur API ${url} :`, error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchData<Article[]>(
      "/api/articles",
      setRecentArticles,
      setLoadingRecentArticles,
    );
    fetchData<Category[]>(
      "/api/categories",
      setCategories,
      setLoadingCategories,
    );
  }, [fetchData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/search?query=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <aside className="w-full space-y-6 border-l p-4 md:w-1/3 lg:w-1/4">
      {/* ✅ Barre de recherche */}
      <div className="rounded-lg bg-gray-100 p-4 shadow dark:bg-gray-800">
        <h3 className="mb-3 text-lg font-bold">Recherche</h3>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Rechercher un article..."
            className="w-full rounded-lg border p-2"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="mt-2 w-full rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-800">
            Rechercher
          </button>
        </form>
      </div>

      {/* ✅ Message d'erreur global */}
      {errorMessage && <p className="text-red-500">{errorMessage}</p>}

      {/* ✅ Articles récents avec SKELETON LOADER */}
      <div className="rounded-lg bg-gray-100 p-4 shadow dark:bg-gray-800">
        <h3 className="mb-3 text-lg font-bold">Articles Récents</h3>
        {loadingRecentArticles ? (
          <div className="space-y-2">
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
        ) : recentArticles.length > 0 ? (
          <ul className="space-y-2">
            {recentArticles.map((article) => (
              <li key={article.id}>
                <Link
                  href={`/${article.slug}`}
                  className="text-blue-600 hover:underline"
                >
                  {article.title}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="italic text-gray-500">
            Aucun article récent disponible.
          </p>
        )}
      </div>

      {/* ✅ Catégories avec SKELETON LOADER */}
      <div className="rounded-lg bg-gray-100 p-4 shadow dark:bg-gray-800">
        <h3 className="mb-3 text-lg font-bold">Catégories</h3>
        {loadingCategories ? (
          <div className="space-y-2">
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
        ) : categories.length > 0 ? (
          <ul className="space-y-2">
            {categories.map((category) => (
              <li key={category.id}>
                <Link
                  href={`/category/${category.slug}`}
                  className="text-blue-600 hover:underline"
                >
                  {category.name}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="italic text-gray-500">Aucune catégorie disponible.</p>
        )}
      </div>
    </aside>
  );
};

export default RightSidebar;
